import {
  AfterViewInit,
  Component,
  effect,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TeamsAutoAttendantsDestinationsSearchService } from '../../../teams-auto-attendants-destinations-search.service';
import { UiDialButton } from '../auto-attendants-menu-buttons/teams-auto-attendants-menu-buttons.component';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import {
  AccountType,
  AutoAttendantAudioMetadata,
  AutoAttendantButtonAction,
  AutoAttendantOperatorType,
  MicrosoftTeamsCallQueueViewEntityStatus,
  VoiceAppViewEntity,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { MicrosoftTeamsUserSearchResource } from '../../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import { Microsoft365GroupResource } from '../../../../helpdesk/shared/resources/microsoft-365-group.resource';
import { MicrosoftCallQueueSearchResource } from '../../../../helpdesk/shared/resources/microsoft-call-queue-search.resource';
import { MicrosoftAutoAttendantsSearchResource } from '../../../../helpdesk/shared/resources/microsoft-auto-attendants-search.resource';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { AudioFileResource } from '../../../../shared/resources/audio-file.resource';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { ZiroFileUploadConfig } from '../../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { Observable, of, Subscription, timer } from 'rxjs';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { take } from 'rxjs/operators';
import { TeamsAutoAttendantsAccountSearchResource } from '../../../resources/teams-auto-attendants-account-search.resource';

const CONFIGURABLE_ACTIONS: AutoAttendantButtonAction[] = [
  AutoAttendantButtonAction.OPERATOR,
  AutoAttendantButtonAction.USER,
  AutoAttendantButtonAction.VOICE_APP,
  AutoAttendantButtonAction.RESOURCE_ACCOUNT,
  AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER,
  AutoAttendantButtonAction.VOICEMAIL,
  AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE,
  AutoAttendantButtonAction.ANNOUNCEMENT_TTS,
];

export interface MenuButtonRowEntity {
  key: string;
  action: AutoAttendantButtonAction;
  destination: string;
  entity: VoiceAppViewEntity;
  audioFile: File | AutoAttendantAudioMetadata;
  voiceCommand: string[];
}

export interface MenuButtonRowFormData {
  action: ZiroSelectOption;
  announcementType: AnnouncementType;
  entitySearch: ZiroSelectOption;
  externalPhoneNumber: string;
  announcementAudio: AutoAttendantAudioMetadata | File;
  announcementTts: string;
  voiceCommand: string;
}

export enum AnnouncementType {
  AUDIO = 'AUDIO',
  TTS = 'TTS',
}

export const E164_REGEX = /^\+[1-9]\d{1,14}$/;

const ENTITY_SEARCH_PLACEHOLDER_KEYS: Partial<Record<AutoAttendantButtonAction, string>> = {
  [AutoAttendantButtonAction.USER]:
    'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination.placeholder.user',
  [AutoAttendantButtonAction.RESOURCE_ACCOUNT]:
    'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination.placeholder.resource_account',
  [AutoAttendantButtonAction.VOICE_APP]:
    'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination.placeholder.voice_app',
  [AutoAttendantButtonAction.VOICEMAIL]:
    'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination.placeholder.voicemail',
};

const DEFAULT_ENTITY_SEARCH_PLACEHOLDER_KEY =
  'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination.placeholder';

@Component({
  selector: 'ziro-menu-button-form',
  templateUrl: './menu-button-form.component.html',
  styleUrl: './menu-button-form.component.scss',
  imports: [ZiroFormsModule, TranslateModule],
  providers: [
    TeamsAutoAttendantsDestinationsSearchService,
    MicrosoftTeamsUserSearchResource,
    Microsoft365GroupResource,
    MicrosoftCallQueueSearchResource,
    MicrosoftAutoAttendantsSearchResource,
  ],
})
export class MenuButtonFormComponent
  extends ZiroFormAbstractDirective<MenuButtonRowEntity, MenuButtonRowFormData>
  implements AfterViewInit, OnChanges, OnDestroy
{
  private _editContext = inject(TeamsAutoAttendantEditContext);
  protected _ziroFormStateService = inject(ZiroFormStateService);

  tenantId = input.required<string>();
  buttonKey = input.required<string>();
  initialButton = input<UiDialButton>(null);

  rowUpdate = output<MenuButtonRowEntity>();

  greetingUploadURL = signal('');
  greetingDownloadName = signal('');
  greetingFileExtension = signal('');

  private _audioFileResource = inject(AudioFileResource);
  private _search = inject(TeamsAutoAttendantsDestinationsSearchService);
  private _translateService = inject(TranslateService);
  private _accountSearchResource = inject(TeamsAutoAttendantsAccountSearchResource);
  private _initialAudioLoaded = false;
  private _entityValidationResult: { upn: string; hasValidLicense: boolean; hasPhoneNumber: boolean } = null;

  private readonly _actionLabelMap: Partial<Record<AutoAttendantButtonAction, string>> = {
    [AutoAttendantButtonAction.OPERATOR]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_operator',
    [AutoAttendantButtonAction.USER]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_user',
    [AutoAttendantButtonAction.VOICE_APP]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_voice_application',
    [AutoAttendantButtonAction.RESOURCE_ACCOUNT]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_resource_account',
    [AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_external_phone_number',
    [AutoAttendantButtonAction.VOICEMAIL]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.transfer_to_voicemail',
    [AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.announcement',
    [AutoAttendantButtonAction.ANNOUNCEMENT_TTS]:
      'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.announcement',
  };
  private readonly _actionToAnnouncementType: Partial<Record<AutoAttendantButtonAction, AnnouncementType>> = {
    [AutoAttendantButtonAction.ANNOUNCEMENT_TTS]: AnnouncementType.TTS,
    [AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE]: AnnouncementType.AUDIO,
  };

  readonly actionOptions: ZiroSelectOption[] = [
    {
      label: this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.do_nothing',
      ),
      value: null,
    },
    ...CONFIGURABLE_ACTIONS.filter((action) => action !== AutoAttendantButtonAction.ANNOUNCEMENT_TTS).map((action) => ({
      label: this._translateService.instant(this._actionLabelMap[action]),
      value: action,
    })),
  ];

  acceptedFileTypes = ['.wav', '.wma', '.mp3'];

  formConfig: ZiroFormConfig = {
    fields: {
      action: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.action',
        dataAutomation: 'menu-button-action',
        helpText: () => this._getHelpTextForAction(),
        componentConfig: new ZiroSelectConfig({
          options: this.actionOptions,
          hideClear: true,
        }),
        validation: [
          {
            validator: (val: ZiroSelectOption) => {
              if (val.value !== AutoAttendantButtonAction.OPERATOR) {
                return ZiroFormsValidationState.VALID;
              }
              const operator = this._editContext.autoAttendant()?.operator;
              const hasOperatorType = !!operator?.type && operator.type !== AutoAttendantOperatorType.NOT_SUPPORTED;
              const hasDestination = !!operator?.destination;
              return hasOperatorType && hasDestination
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message:
              'tkey;admin.teams_voice_management.teams_auto_attendant.call_flow.button.operator.no_operator_configured.error',
          },
        ],
      },
      entitySearch: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination',
        dataAutomation: 'menu-button-entity-search',
        hidden: () => !this._isSearchAction(this.formData?.action?.value),
        valExcluded: () => !this._isSearchAction(this.formData?.action?.value),
        required: () => this._isSearchAction(this.formData?.action?.value),
        componentConfig: this._buildEntitySearchConfig(null),
        validation: [
          {
            validator: (val: ZiroSelectOption) => {
              if (!val) {
                return ZiroFormsValidationState.VALID;
              }
              const action = this.formData?.action?.value;
              if (action !== AutoAttendantButtonAction.VOICE_APP && action !== AutoAttendantButtonAction.VOICEMAIL) {
                return ZiroFormsValidationState.VALID;
              }
              const entity = val.value as VoiceAppViewEntity;
              return entity?.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: () => ({
              content: 'tkey;admin.teams_voice_management.teams_auto_attendant.validation.button.destination.not_found',
              params: { name: this.formData?.entitySearch?.label ?? '' },
            }),
          },
          {
            validator: (val: ZiroSelectOption) => {
              if (!val || this.formData?.action?.value !== AutoAttendantButtonAction.USER) {
                return ZiroFormsValidationState.VALID;
              }
              if (!this._entityValidationResult) {
                return ZiroFormsValidationState.VALID;
              }
              const entityVal = val.value;
              const upn = entityVal?.['userPrincipalName'] ?? entityVal?.['upn'] ?? null;
              if (upn && this._entityValidationResult.upn.toLowerCase() !== upn.toLowerCase()) {
                return ZiroFormsValidationState.VALID;
              }
              return !this._entityValidationResult.hasValidLicense || !this._entityValidationResult.hasPhoneNumber
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: () => ({
              content: 'tkey;admin.teams_voice_management.teams_auto_attendant.validation.operator.user.no_ev',
              params: { name: this.formData?.entitySearch?.label ?? '' },
            }),
          },
        ],
        misconfigurationFeedback: [
          {
            validator: (val: ZiroSelectOption) => {
              if (!val || this.formData?.action?.value !== AutoAttendantButtonAction.RESOURCE_ACCOUNT) {
                return ZiroFormsValidationState.VALID;
              }
              if (!this._entityValidationResult) {
                return ZiroFormsValidationState.VALID;
              }
              const entityVal = val.value;
              const upn = entityVal?.['upn'] ?? entityVal?.['userPrincipalName'] ?? null;
              if (upn && this._entityValidationResult.upn.toLowerCase() !== upn.toLowerCase()) {
                return ZiroFormsValidationState.VALID;
              }
              return !this._entityValidationResult.hasValidLicense || !this._entityValidationResult.hasPhoneNumber
                ? ZiroFormsValidationState.WARNING
                : ZiroFormsValidationState.VALID;
            },
            message: () => ({
              content:
                'tkey;admin.teams_voice_management.teams_auto_attendant.validation.operator.resource_account.no_license_or_phone',
              params: { name: this.formData?.entitySearch?.label ?? '' },
            }),
          },
        ],
      },
      externalPhoneNumber: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.destination',
        dataAutomation: 'menu-button-external-number',
        hidden: () => this.formData?.action?.value !== AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER,
        valExcluded: () => this.formData?.action?.value !== AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER,
        required: () => this.formData?.action?.value === AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER,
        validation: [
          {
            validator: (val: string) =>
              !val || E164_REGEX.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
            message: 'tkey;validators.global.error.invalid_number.e164_pattern',
          },
        ],
      },
      announcementType: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.announcement.type.label',
        dataAutomation: 'menu-button-announcement-type',
        hidden: () => !this._isAnnouncementAction(this.formData?.action?.value),
        valExcluded: () => !this._isAnnouncementAction(this.formData?.action?.value),
        required: () => this._isAnnouncementAction(this.formData?.action?.value),
        componentConfig: new ZiroRadioConfig<AnnouncementType>({
          inline: true,
          buttons: [
            {
              label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.announcement.audio.label',
              value: AnnouncementType.AUDIO,
            },
            {
              label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.announcement.tts.label',
              value: AnnouncementType.TTS,
            },
          ],
        }),
      },
      announcementAudio: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.announcement.audio.file.upload.label',
        dataAutomation: 'menu-button-announcement-audio',
        hidden: () => !this._isAnnouncementAudio(),
        valExcluded: () => !this._isAnnouncementAudio(),
        required: () => this._isAnnouncementAudio(),
        componentConfig: this._buildFileUploadConfig(),
      },
      announcementTts: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.announcement.tts.text_area.label',
        dataAutomation: 'menu-button-announcement-tts',
        componentConfig: new ZiroTextareaConfig({}),
        hidden: () => !this._isAnnouncementTts(),
        valExcluded: () => !this._isAnnouncementTts(),
        required: () => this._isAnnouncementTts(),
      },
      voiceCommand: {
        label:
          'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.voice_command.label',
        dataAutomation: 'menu-button-voice-command',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        hidden: () => !this._editContext.autoAttendant().voiceResponseEnabled,
        valExcluded: () => !this._editContext.autoAttendant().voiceResponseEnabled,
      },
    },
    options: {
      columnClasses: {
        label: 'text-start',
        input: '',
      },
    },
  };

  constructor() {
    super();
    effect(() => {
      const operator = this._editContext.autoAttendant()?.operator;
      if (operator) {
        this.fieldChannels?.['action']?.validateSource.next();
      }
    });
    effect(() => {
      void this._editContext.autoAttendant().voiceResponseEnabled;
      this._setFieldStates();
    });
  }

  private _subscriptions = new Subscription();
  private _lastHydratedButtonKey: string = null;
  private _lastHydratedAction: AutoAttendantButtonAction = null;
  private _lastHydratedDestination = '';

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (!changes['initialButton']) {
      return;
    }

    const btn = this.initialButton();
    const incomingAction = btn?.action ?? null;
    const incomingDestination = btn?.destination ?? '';
    const incomingKey = this.buttonKey();

    const isSameHydratedValue =
      this._lastHydratedButtonKey === incomingKey &&
      this._lastHydratedAction === incomingAction &&
      this._lastHydratedDestination === incomingDestination;

    if (isSameHydratedValue) {
      return;
    }
    if (this._lastHydratedButtonKey !== incomingKey) {
      this._initialAudioLoaded = false;
    }
    this._lastHydratedButtonKey = incomingKey;
    this._lastHydratedAction = incomingAction;
    this._lastHydratedDestination = incomingDestination;

    const isAudioFileAction = incomingAction === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE;
    const hasPendingAudioDownload = isAudioFileAction && !!btn?.audioInfo?.downloadUri;

    const audioFileId = btn?.audioInfo?.downloadUri?.split('/').pop() ?? null;
    this.entitySource.next({
      key: incomingKey,
      action: incomingAction,
      destination: incomingDestination,
      entity: btn?.entity ?? null,
      audioFile: hasPendingAudioDownload
        ? {
            id: audioFileId,
            downloadUri: btn.audioInfo.downloadUri,
            name: btn.audioInfo.fileName,
          }
        : (btn?.audioFile ?? null),
      voiceCommand: btn?.voiceCommand ?? [],
    });

    const isEntityAction =
      incomingAction === AutoAttendantButtonAction.USER ||
      incomingAction === AutoAttendantButtonAction.RESOURCE_ACCOUNT;
    if (isEntityAction && btn?.entity?.upn) {
      this._validateButtonEntity(btn.entity.upn, incomingAction);
    } else if (!isEntityAction) {
      this._entityValidationResult = null;
    }

    if (hasPendingAudioDownload && !this._initialAudioLoaded) {
      this._initialAudioLoaded = true;

      const audioId = btn.audioInfo.downloadUri.split('/').pop();
      const requestedButtonKey = incomingKey;
      const requestedAction = incomingAction;

      const audioSub = this._audioFileResource.getAudioFile(this.tenantId(), 'auto-attendant', audioId).subscribe({
        next: (file) => {
          const currentAction = this.formData?.action?.value ?? null;
          const userChangedAction = currentAction !== requestedAction;
          const buttonChanged = this.buttonKey() !== requestedButtonKey;

          if (userChangedAction || buttonChanged) {
            return;
          }

          this._setGreetingPreview(file);
        },
      });

      this._subscriptions.add(audioSub);
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    const actionSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<MenuButtonRowEntity>) => {
      const newAction = update.new?.action ?? null;
      const oldAction = update.old?.action ?? null;
      if (!update.old || newAction === oldAction) {
        return;
      }
      this.fieldChannels?.['entitySearch']?.componentConfigSource.next(this._buildEntitySearchConfig(newAction));
      this._entityValidationResult = null;
      this.entitySource.next({
        key: this.buttonKey(),
        action: newAction,
        destination: '',
        entity: null,
        audioFile: null,
        voiceCommand: this.formData.voiceCommand ? [this.formData.voiceCommand] : [],
      });
    });
    this._subscriptions.add(actionSub);

    const fieldSubscriptionsSub = timer(0).subscribe(() => {
      this._subscriptions.add(
        this.fieldChannels['entitySearch'].ziroFormUpdate$.subscribe((update) => {
          const newValue = update.new?.value;
          const oldValue = update.old?.value;
          const action = this.formData?.action?.value;
          if (
            newValue &&
            (action === AutoAttendantButtonAction.USER || action === AutoAttendantButtonAction.RESOURCE_ACCOUNT)
          ) {
            const upn = newValue['upn'] ?? newValue['userPrincipalName'] ?? null;
            const previousUpn = oldValue?.['upn'] ?? oldValue?.['userPrincipalName'] ?? null;
            if (upn && upn !== previousUpn) {
              this._validateButtonEntity(upn, action);
            }
          } else {
            this._entityValidationResult = null;
          }
        }),
      );

      this._subscriptions.add(
        this.fieldChannels['announcementAudio'].ziroFormUpdate$.subscribe(
          (update: ZiroFormsUpdate<File | AutoAttendantAudioMetadata>) => {
            if (update.new instanceof File) {
              this._setAudioPreview(update.new);
            } else if (update.new) {
              const metadata = update.new as AutoAttendantAudioMetadata;
              const fileName = metadata.name ?? '';
              const lastDot = fileName.lastIndexOf('.');
              this.greetingFileExtension.set(lastDot >= 0 ? fileName.substring(lastDot) : '');
              this.greetingDownloadName.set(fileName);
              this.greetingUploadURL.set(metadata.downloadUri ?? '');
            } else {
              this._clearGreetingPreview();
            }
          },
        ),
      );
    });
    this._subscriptions.add(fieldSubscriptionsSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  toFormData = (entity: MenuButtonRowEntity): MenuButtonRowFormData => {
    const originalAction = entity?.action ?? null;
    const isAnnouncement = this._isAnnouncementAction(originalAction);
    const buttonAction = isAnnouncement ? AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE : originalAction;
    const matchedActionOption = this.actionOptions.find((opt) => opt.value === buttonAction);

    return {
      action: matchedActionOption,
      announcementType: this._actionToAnnouncementType[originalAction] ?? null,
      entitySearch:
        this._isSearchAction(buttonAction) && entity?.entity
          ? {
              label:
                entity.entity.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND
                  ? entity.entity.guid
                  : (entity.entity.displayName ?? ''),
              value: entity.entity,
            }
          : null,
      externalPhoneNumber:
        buttonAction === AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER ? (entity?.destination ?? '') : '',
      announcementAudio: entity?.audioFile instanceof File ? entity.audioFile : (entity?.audioFile ?? null),
      announcementTts: originalAction === AutoAttendantButtonAction.ANNOUNCEMENT_TTS ? (entity?.destination ?? '') : '',
      voiceCommand: entity?.voiceCommand[0] ?? '',
    };
  };

  toEntity = (formData: MenuButtonRowFormData): MenuButtonRowEntity => {
    let entityAction = formData.action?.value ?? null;
    let destination = '';
    let entity: VoiceAppViewEntity = null;
    let audioFile: File | AutoAttendantAudioMetadata = null;

    if (this._isSearchAction(entityAction) && formData.entitySearch) {
      entity = formData.entitySearch.value ?? null;
      destination = entity?.displayName ?? '';
    } else if (entityAction === AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER) {
      destination = formData.externalPhoneNumber ?? '';
    } else if (this._isAnnouncementAction(entityAction)) {
      if (formData.announcementType === AnnouncementType.TTS) {
        entityAction = AutoAttendantButtonAction.ANNOUNCEMENT_TTS;
        destination = formData.announcementTts ?? '';
        audioFile = null;
      } else {
        entityAction = AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE;
        audioFile = formData.announcementAudio ?? null;
        destination =
          audioFile && !(audioFile instanceof File) ? this._getAudioFileIdFromDownloadUri(audioFile.downloadUri) : '';
      }
    }

    return {
      key: this.buttonKey(),
      action: entityAction,
      destination,
      entity,
      audioFile,
      voiceCommand: formData.voiceCommand ? [formData.voiceCommand] : [],
    };
  };

  protected submit(): Observable<any> {
    return of(null);
  }

  protected get isActionOnlyButton(): boolean {
    return (
      this.formData?.action?.value === null ||
      this.formData?.action?.value === AutoAttendantButtonAction.OPERATOR ||
      this.formData?.action?.value === AutoAttendantButtonAction.ANNOUNCEMENT_TTS ||
      this.formData?.action?.value === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE
    );
  }

  protected get shouldShowAnnouncementType(): boolean {
    return this._isAnnouncementAction(this.formData?.action?.value);
  }

  protected get shouldShowAnnouncementAudio(): boolean {
    return this._isAnnouncementAudio();
  }

  protected get shouldShowAnnouncementTts(): boolean {
    return this._isAnnouncementTts();
  }

  private _isSearchAction(action: AutoAttendantButtonAction): boolean {
    return (
      action === AutoAttendantButtonAction.USER ||
      action === AutoAttendantButtonAction.VOICE_APP ||
      action === AutoAttendantButtonAction.RESOURCE_ACCOUNT ||
      action === AutoAttendantButtonAction.VOICEMAIL
    );
  }

  private _searchForCurrentAction(term: string): Observable<ZiroSelectOption[]> {
    const tenantId = this.tenantId();
    switch (this.formData?.action?.value) {
      case AutoAttendantButtonAction.USER:
        return this._search.searchUsers(tenantId, term);
      case AutoAttendantButtonAction.VOICE_APP:
        return this._search.searchVoiceApps(tenantId, term);
      case AutoAttendantButtonAction.RESOURCE_ACCOUNT:
        return this._search.searchResourceAccounts(tenantId, term);
      case AutoAttendantButtonAction.VOICEMAIL:
        return this._search.searchVoicemailGroups(tenantId, term);
      default:
        return of([]);
    }
  }

  private _isAnnouncementAction(action: AutoAttendantButtonAction): boolean {
    return (
      action === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE ||
      action === AutoAttendantButtonAction.ANNOUNCEMENT_TTS
    );
  }

  private _buildEntitySearchConfig(action: AutoAttendantButtonAction | null): ZiroSelectConfig {
    const placeholderKey = (action && ENTITY_SEARCH_PLACEHOLDER_KEYS[action]) || DEFAULT_ENTITY_SEARCH_PLACEHOLDER_KEY;

    return new ZiroSelectConfig({
      minSearchLength: 3,
      placeholder: this._translateService.instant(placeholderKey),
      asyncOptionsFn: (term: string) => this._searchForCurrentAction(term),
    });
  }

  private _setAudioPreview(file: File): void {
    this.fieldChannels?.['announcementAudio']?.componentConfigSource?.next(this._buildFileUploadConfig());
    this._setGreetingPreview(file);
  }

  private _setGreetingPreview(file: File): void {
    const lastDot = file.name.lastIndexOf('.');
    this.greetingFileExtension.set(file.name.substring(lastDot));
    this.greetingUploadURL.set(URL.createObjectURL(file));
    this.greetingDownloadName.set(file.name);
  }

  private _getHelpTextForAction(): string {
    return this.formData?.action?.value === null
      ? 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.action.do_nothing.helptext'
      : '';
  }

  private _getAudioFileIdFromDownloadUri(downloadUri: string | null | undefined): string {
    if (!downloadUri) {
      return '';
    }
    return downloadUri.split('?')[0].split('/').pop() ?? '';
  }

  private _validateButtonEntity(upn: string, action: AutoAttendantButtonAction): void {
    const accountType = action === AutoAttendantButtonAction.USER ? AccountType.USER : AccountType.RESOURCE_ACCOUNT;
    const sub = this._accountSearchResource
      .search(this.tenantId(), upn, accountType)
      .pipe(take(1))
      .subscribe((results) => {
        const result = results.find((r) => r.userPrincipalName.toLowerCase() === upn.toLowerCase());
        if (!result) {
          return;
        }
        this._entityValidationResult = {
          upn,
          hasValidLicense: result.hasValidLicense,
          hasPhoneNumber: result.hasPhoneNumber,
        };
        this.fieldChannels?.['entitySearch']?.validateSource.next();
      });
    this._subscriptions.add(sub);
  }

  private _isAnnouncementAudio = (): boolean =>
    this._isAnnouncementAction(this.formData?.action?.value) &&
    this.formData?.announcementType === AnnouncementType.AUDIO;

  private _isAnnouncementTts = (): boolean =>
    this._isAnnouncementAction(this.formData?.action?.value) &&
    this.formData?.announcementType === AnnouncementType.TTS;

  private _buildFileUploadConfig(): ZiroFileUploadConfig {
    return new ZiroFileUploadConfig({
      acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
      isAudioFile: true,
    });
  }

  private _clearGreetingPreview(): void {
    this.greetingUploadURL.set('');
    this.greetingDownloadName.set('');
    this.greetingFileExtension.set('');
  }
}
